import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  Chip,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import {
  collection,
  doc,
  limit as fsLimit,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { agencyCredits } from './Agencias';

const PLAN_COLOR = { rookie: 'default', silver: 'default', gold: 'warning', titanium: 'primary' };
const COLLECTIONS = [
  'agency_events',
  'agency_recommended_actions',
  'agency_members',
  'agency_profiles',
  'agency_overlays',
  'agency_training',
  'agency_manager_assignments',
  'agency_manager_credits'
];

function formatTs(value) {
  if (!value) return '—';
  let d;
  if (typeof value?.toDate === 'function') d = value.toDate();
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string') d = new Date(value);
  else return '—';
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatDate(value) {
  if (!value) return '—';
  let d;
  if (typeof value?.toDate === 'function') d = value.toDate();
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string') d = new Date(value);
  else return '—';
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Devuelve la función `unsubscribe`. Hace push a `setMap` con la última
// snapshot de la colección filtrada por agencyId.
function subscribeAgencyCollection(name, agencyId, setMap) {
  const q = query(
    collection(db, name),
    where('agencyId', '==', agencyId),
    fsLimit(2000)
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMap((prev) => ({ ...prev, [name]: rows }));
    },
    (err) => {
      console.warn(`[AgenciaDetail] ${name} subscribe error:`, err.message);
      setMap((prev) => ({ ...prev, [name]: prev[name] || [] }));
    }
  );
}

export default function AgenciaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, isViewer } = useAuth();

  const [agency, setAgency] = useState(null);
  const [collections, setCollections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Suscripciones en tiempo real: 1 al doc agencia + 8 a colecciones
  // relacionadas. Cada `onSnapshot` empuja a su clave del map de
  // collections. Cleanup individual al desmontar o cambiar de agencia.
  useEffect(() => {
    setLoading(true);
    setError(null);
    setCollections({});

    const unsubs = [];

    // Doc principal de agencia.
    unsubs.push(
      onSnapshot(
        doc(db, 'agencies', id),
        (docSnap) => {
          if (!docSnap.exists()) {
            setError(`Agencia ${id} no encontrada en Firestore.`);
            setAgency(null);
          } else {
            setAgency({ id: docSnap.id, ...docSnap.data() });
            setError(null);
          }
          setLoading(false);
        },
        (err) => {
          console.error('[AgenciaDetail] agency doc error:', err);
          setError(err.message || 'Error cargando agencia');
          setLoading(false);
        }
      )
    );

    // 8 colecciones relacionadas.
    for (const name of COLLECTIONS) {
      unsubs.push(subscribeAgencyCollection(name, id, setCollections));
    }

    return () => unsubs.forEach((fn) => fn && fn());
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Cargando agencia…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <Button size="sm" variant="flat" onPress={() => navigate('/agencias')}>
          ← Volver a Agencias
        </Button>
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      </div>
    );
  }

  const events = collections.agency_events || [];
  const missions = collections.agency_recommended_actions || [];
  const members = collections.agency_members || [];
  const profiles = collections.agency_profiles || [];
  const overlays = collections.agency_overlays || [];
  const training = collections.agency_training || [];
  const managerAssignments = collections.agency_manager_assignments || [];
  const managerCredits = collections.agency_manager_credits || [];

  const activeEvents = events.filter((e) => e.active && !e.draft);
  const draftEvents = events.filter((e) => e.draft);
  const pastEvents = events.filter((e) => !e.active && !e.draft);

  const missionsByStatus = missions.reduce((acc, m) => {
    const s = m.status || 'unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button size="sm" variant="flat" onPress={() => navigate('/agencias')}>
          ← Volver a Agencias
        </Button>
      </div>

      {/* Header */}
      <div className="rounded-lg border border-default-200 bg-content1 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="text-4xl">{agency.logo || '🏢'}</div>
            <div>
              <h1 className="text-2xl font-semibold">{agency.name || id}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-default-500">
                <code className="text-xs">{id}</code>
                {agency.region && <span>· {agency.region}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Chip
                  size="sm"
                  variant="flat"
                  color={PLAN_COLOR[agency.plan] || 'default'}
                >
                  plan: {agency.plan || 'silver'}
                </Chip>
                <Chip size="sm" variant="flat">
                  {agencyCredits(agency)} créditos{agency.planOverrides ? ' *' : ''}
                </Chip>
                {agency.validated ? (
                  <Chip size="sm" color="success" variant="flat">
                    Validada
                  </Chip>
                ) : (
                  <Chip size="sm" color="warning" variant="flat">
                    Pendiente
                  </Chip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Eventos"
          value={events.length}
          sub={`${activeEvents.length} activos · ${draftEvents.length} borrador · ${pastEvents.length} pasados`}
        />
        <StatCard
          label="Misiones"
          value={missions.length}
          sub={Object.entries(missionsByStatus)
            .map(([s, n]) => `${s}: ${n}`)
            .join(' · ') || '—'}
        />
        <StatCard label="Miembros" value={members.length} />
        <StatCard label="Perfiles" value={profiles.length} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Overlays" value={overlays.length} />
        <StatCard label="Formación" value={training.length} />
        {canWrite && (
          <>
            <StatCard label="Asig. managers" value={managerAssignments.length} />
            <StatCard label="Créditos managers" value={managerCredits.length} />
          </>
        )}
      </div>

      <Accordion
        selectionMode="multiple"
        defaultExpandedKeys={['events', 'missions']}
        variant="splitted"
      >
        {/* EVENTOS */}
        <AccordionItem
          key="events"
          title={
            <span>
              Eventos <span className="text-default-400">· {events.length}</span>
            </span>
          }
        >
          {events.length ? (
            <EventsTable events={events} />
          ) : (
            <p className="text-sm text-default-500">Sin eventos.</p>
          )}
        </AccordionItem>

        {/* MISIONES */}
        <AccordionItem
          key="missions"
          title={
            <span>
              Misiones{' '}
              <span className="text-default-400">· {missions.length}</span>
            </span>
          }
        >
          {missions.length ? (
            <MissionsTable items={missions} />
          ) : (
            <p className="text-sm text-default-500">Sin misiones recomendadas.</p>
          )}
        </AccordionItem>

        {/* MIEMBROS — viewer solo ve count, no la lista */}
        <AccordionItem
          key="members"
          title={
            <span>
              Miembros{' '}
              <span className="text-default-400">· {members.length}</span>
            </span>
          }
        >
          {isViewer ? (
            <p className="text-sm text-default-500">
              Esta agencia tiene <strong>{members.length}</strong> miembros. La
              lista detallada está restringida a admins completos.
            </p>
          ) : members.length ? (
            <GenericTable
              rows={members}
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'userId', label: 'UID' },
                { key: 'tiktokUsername', label: 'TikTok' },
                { key: 'role', label: 'Rol' },
                { key: 'status', label: 'Estado' },
                { key: 'joinedAt', label: 'Unido', type: 'ts' }
              ]}
            />
          ) : (
            <p className="text-sm text-default-500">Sin miembros.</p>
          )}
        </AccordionItem>

        {/* PERFILES */}
        <AccordionItem
          key="profiles"
          title={
            <span>
              Perfiles{' '}
              <span className="text-default-400">· {profiles.length}</span>
            </span>
          }
        >
          {profiles.length ? (
            <GenericTable
              rows={profiles}
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'name', label: 'Nombre' },
                { key: 'createdAt', label: 'Creado', type: 'ts' },
                { key: 'updatedAt', label: 'Actualizado', type: 'ts' }
              ]}
            />
          ) : (
            <p className="text-sm text-default-500">Sin perfiles.</p>
          )}
        </AccordionItem>

        {/* OVERLAYS */}
        <AccordionItem
          key="overlays"
          title={
            <span>
              Overlays{' '}
              <span className="text-default-400">· {overlays.length}</span>
            </span>
          }
        >
          {overlays.length ? (
            <GenericTable
              rows={overlays}
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'name', label: 'Nombre' },
                { key: 'type', label: 'Tipo' },
                { key: 'updatedAt', label: 'Actualizado', type: 'ts' }
              ]}
            />
          ) : (
            <p className="text-sm text-default-500">Sin overlays.</p>
          )}
        </AccordionItem>

        {/* FORMACIÓN */}
        <AccordionItem
          key="training"
          title={
            <span>
              Formación{' '}
              <span className="text-default-400">· {training.length}</span>
            </span>
          }
        >
          {training.length ? (
            <GenericTable
              rows={training}
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'title', label: 'Título' },
                { key: 'type', label: 'Tipo' },
                { key: 'createdAt', label: 'Creado', type: 'ts' }
              ]}
            />
          ) : (
            <p className="text-sm text-default-500">Sin materiales.</p>
          )}
        </AccordionItem>

        {/* MANAGER ASSIGNMENTS — solo admin completo */}
        {canWrite && (
          <AccordionItem
            key="managers"
            title={
              <span>
                Asignaciones managers{' '}
                <span className="text-default-400">
                  · {managerAssignments.length}
                </span>
              </span>
            }
          >
            {managerAssignments.length ? (
              <GenericTable
                rows={managerAssignments}
                columns={[
                  { key: 'id', label: 'ID' },
                  { key: 'managerUid', label: 'Manager UID' },
                  { key: 'streamerUid', label: 'Streamer UID' },
                  { key: 'createdAt', label: 'Creado', type: 'ts' }
                ]}
              />
            ) : (
              <p className="text-sm text-default-500">Sin asignaciones.</p>
            )}
          </AccordionItem>
        )}

        {/* RAW — solo admin completo (puede contener UIDs y refs sensibles) */}
        {canWrite && (
        <AccordionItem key="raw" title="JSON completo">
          <Tabs size="sm" aria-label="Raw data">
            <Tab key="agency" title="Agencia">
              <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
                {JSON.stringify(agency, null, 2)}
              </pre>
            </Tab>
            {COLLECTIONS.map((c) => (
              <Tab key={c} title={`${c} (${collections[c]?.length || 0})`}>
                <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
                  {JSON.stringify(collections[c], null, 2)}
                </pre>
              </Tab>
            ))}
          </Tabs>
        </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}

function EventsTable({ events }) {
  return (
    <Table aria-label="Eventos" removeWrapper isStriped>
      <TableHeader>
        <TableColumn>Evento</TableColumn>
        <TableColumn>Inicio</TableColumn>
        <TableColumn>Fin</TableColumn>
        <TableColumn>Estado</TableColumn>
        <TableColumn>Perfil</TableColumn>
        <TableColumn>Ranking groups</TableColumn>
      </TableHeader>
      <TableBody>
        {events.map((ev) => {
          const finished = (() => {
            const d = ev.endDate?.toDate?.() || (ev.endDate ? new Date(ev.endDate) : null);
            if (!d || isNaN(d.getTime())) return false;
            return d < new Date();
          })();
          const groups = Array.isArray(ev.rankingGroups) ? ev.rankingGroups.length : 0;
          return (
            <TableRow key={ev.id}>
              <TableCell>
                <div className="flex items-start gap-2">
                  <span className="text-lg">{ev.icon || '🎯'}</span>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {stripHtml(ev.name) || '—'}
                    </span>
                    <span className="text-xs text-default-400">{ev.id}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>{formatDate(ev.startDate)}</TableCell>
              <TableCell>{formatDate(ev.endDate)}</TableCell>
              <TableCell>
                {ev.draft ? (
                  <Chip size="sm" variant="flat" color="default">
                    Borrador
                  </Chip>
                ) : finished ? (
                  <Chip size="sm" variant="flat" color="default">
                    Finalizado
                  </Chip>
                ) : ev.active ? (
                  <Chip size="sm" variant="flat" color="success">
                    Activo
                  </Chip>
                ) : (
                  <Chip size="sm" variant="flat" color="warning">
                    Inactivo
                  </Chip>
                )}
              </TableCell>
              <TableCell>
                {ev.profileId ? (
                  <code className="text-xs">{ev.profileId}</code>
                ) : (
                  <span className="text-default-400">—</span>
                )}
              </TableCell>
              <TableCell>
                {groups > 0 ? (
                  <Chip size="sm" variant="flat">{groups}</Chip>
                ) : (
                  <span className="text-default-400">—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MissionsTable({ items }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="flex flex-col gap-2">
      <Table aria-label="Misiones" removeWrapper isStriped>
        <TableHeader>
          <TableColumn>Tipo</TableColumn>
          <TableColumn>Estado</TableColumn>
          <TableColumn>Enviado por</TableColumn>
          <TableColumn>Email remitente</TableColumn>
          <TableColumn>Creado</TableColumn>
          <TableColumn align="end">Acciones</TableColumn>
        </TableHeader>
        <TableBody>
          {items.map((m) => (
            <TableRow key={m.id}>
              <TableCell>
                <Chip size="sm" variant="flat">{m.type || '—'}</Chip>
              </TableCell>
              <TableCell>
                <Chip
                  size="sm"
                  variant="flat"
                  color={
                    m.status === 'pending'
                      ? 'warning'
                      : m.status === 'accepted' || m.status === 'done'
                      ? 'success'
                      : m.status === 'rejected'
                      ? 'danger'
                      : 'default'
                  }
                >
                  {m.status || '—'}
                </Chip>
              </TableCell>
              <TableCell>
                <Chip size="sm" variant="flat">
                  {m.senderType || '—'}
                </Chip>
              </TableCell>
              <TableCell>{m.senderEmail || '—'}</TableCell>
              <TableCell>{formatTs(m.createdAt)}</TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() =>
                      setExpanded(expanded?.id === m.id ? null : m)
                    }
                  >
                    {expanded?.id === m.id ? 'Ocultar' : 'JSON'}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {expanded && (
        <pre className="max-h-[40vh] overflow-auto rounded bg-default-100 p-3 text-xs">
          {JSON.stringify(expanded, null, 2)}
        </pre>
      )}
    </div>
  );
}

function GenericTable({ rows, columns }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="flex flex-col gap-2">
      <Table removeWrapper isStriped aria-label="rows">
        <TableHeader>
          {[
            ...columns.map((c) => <TableColumn key={c.key}>{c.label}</TableColumn>),
            <TableColumn key="_actions" align="end">Acciones</TableColumn>
          ]}
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.id || i}>
              {columns.map((c) => {
                const v = r[c.key];
                if (c.type === 'ts') return <TableCell key={c.key}>{formatTs(v)}</TableCell>;
                if (v == null || v === '')
                  return (
                    <TableCell key={c.key}>
                      <span className="text-default-400">—</span>
                    </TableCell>
                  );
                if (typeof v === 'object')
                  return (
                    <TableCell key={c.key}>
                      <code className="text-xs text-default-500">
                        {JSON.stringify(v).slice(0, 40)}…
                      </code>
                    </TableCell>
                  );
                return (
                  <TableCell key={c.key}>
                    {c.key.endsWith('Uid') || c.key === 'id' || c.key === 'userId' ? (
                      <code className="text-xs">{String(v)}</code>
                    ) : (
                      String(v)
                    )}
                  </TableCell>
                );
              })}
              <TableCell>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() =>
                      setExpanded(expanded?.id === r.id ? null : r)
                    }
                  >
                    {expanded?.id === r.id ? 'Ocultar' : 'JSON'}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {expanded && (
        <pre className="max-h-[40vh] overflow-auto rounded bg-default-100 p-3 text-xs">
          {JSON.stringify(expanded, null, 2)}
        </pre>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-default-200 bg-content1 p-4">
      <div className="text-xs uppercase tracking-wider text-default-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-default-500">{sub}</div>}
    </div>
  );
}

